import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	chartLineIcon,
	trashAltIcon,
} from "../../../../core/elements/icons/index.js";
import { formatAsDateAndTime } from "../../../../core/helpers/date-formatting-util.js";
import { formatCurrency } from "../../../../core/helpers/currency-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import badge from "../../../../core/elements/badge/badge.js";
import spacer from "../../../../core/elements/spacer/spacer.js";

/**
 * Widget displaying a forecast archive card, containing the forecast name
 * and its last edited date.
 *
 * @param {ForecastTemplate|Forecast} forecast - The model to render.
 * @param {function(): Promise} [onDeletionRequest] - The promise
 *   should resolve when `forecast` has been deleted. When not
 *   provided, no delete action is shown
 * @param {function(): Promise} [onEstimateTagChangeRequest] - The promise
 *   should resolve when `forecast` has been tagged or untagged as Estimate.
 *   When not provided, no tag action is shown
 * @param {string} forecastUrl - The URL where the user is expected to
 *   go when clicking the card
 */
export default class ForecastArchiveCardWidget extends Widget {
	constructor({
		forecast,
		onDeletionRequest,
		onEstimateTagChangeRequest,
		forecastUrl,
	}) {
		super(...arguments);

		this._forecast = forecast;
		this._onDeletionRequest = onDeletionRequest;
		this._onEstimateTagChangeRequest = onEstimateTagChangeRequest;
		this._forecastUrl = forecastUrl;
		this._summaryValue = this._getForecastSummary()?.getValue();
	}

	renderContentOn(html) {
		html.div(
			{ class: "forecast-card", "data-test": "forecast" },
			this._renderHeaderOn.bind(this),
			this._renderBodyOn.bind(this),
			this._renderFooterOn.bind(this),
		);
	}

	_getForecastSummary() {
		const summaryValues = this._forecast.getSummaryValues();
		if (!summaryValues?.length) return undefined;
		return summaryValues[0];
	}

	_renderBodyOn(html) {
		html.a(
			{
				class: "body",
				"data-test": "body",
				href: this._forecastUrl,
				title: this._forecast.getName(),
			},
			this._renderTotalOn.bind(this),
		);
	}

	_renderTotalOn(html) {
		// this._summaryValue can be 0 and be rendered anyway
		if (this._summaryValue === undefined) return;

		html.div(
			{
				"data-test": "total",
				class: "total",
			},
			html.span(
				{
					"data-test": "total-value",
					class: "value",
				},
				formatCurrency(this._summaryValue),
			),
			html.span(
				{ class: "result-icon" },
				this._summaryValue >= 0
					? html.i({ class: "fa-solid fa-circle-up" })
					: html.i({ class: "fa-solid fa-circle-down" }),
			),
			this._renderResultOn.bind(this),
		);
	}

	_renderResultOn(html) {
		const year = this._getFiscalYearName();

		html.div(
			{
				class: "result",
			},
			this._summaryValue >= 0
				? html.span(
						{ class: "text-profit" },
						_("Forecasted profit for {year}", { year }),
					)
				: html.span(
						{ class: "text-loss" },
						_("Forecasted loss for {year}", { year }),
					),
		);
	}

	_getFirstPredictedYear() {
		return this._forecast.getPredictedPeriods()[0].getYear();
	}

	_getFiscalYearName() {
		const { openingPeriod, closingPeriod } =
			this._getOpeningAndClosingPeriods();

		if (!closingPeriod) return this._getFirstPredictedYear();

		const openingYear = openingPeriod?.getYear();
		const closingYear = closingPeriod.getYear();

		return openingYear === closingYear
			? closingYear
			: `${openingYear}-${closingYear}`;
	}

	_getOpeningAndClosingPeriods() {
		// Reversing the periods order to search from the future to the past
		const periods = [...this._forecast.getAllPeriods()].reverse();
		const summaryPeriod = this._getForecastSummary().getPeriod();

		const closingIndex = periods.findIndex((period) =>
			period.equal(summaryPeriod),
		);

		const openingPeriod = periods.find((period, index) => {
			if (index <= closingIndex) return false;
			return this._isOpeningPeriod(period);
		});

		return { openingPeriod, closingPeriod: periods[closingIndex] };
	}

	_isOpeningPeriod(period) {
		return period.getIndex() === 1;
	}

	_renderHeaderOn(html) {
		html.div(
			{ class: "forecast-card-header" },
			this._renderTagsOn.bind(this),
			this._renderActionsMenuOn.bind(this),
		);
	}

	_renderTagsOn(html) {
		if (!this._forecast.isEstimate()) {
			return;
		}

		html.div(
			{ "data-test": "estimate", class: "pull-left forecast-tags" },
			badge({
				label: _("Estimate"),
			}),
		);
	}

	_renderActionsMenuOn(html) {
		if (!this._onDeletionRequest && !this._onEstimateTagChangeRequest) {
			return;
		}

		html.render(
			spacer(
				{ class: "actions-menu pull-right" },
				this._renderTagButtonOn(html),
				this._renderDeleteButtonOn(html),
			),
		);
	}

	_renderTagButtonOn(html) {
		return (
			this._onEstimateTagChangeRequest &&
			html.div(
				{
					class: [
						"action-button",
						{
							"tag-button": !this._forecast.isEstimate(),
							"untag-button": this._forecast.isEstimate(),
						},
					],
					"data-test": "tag",
					click: (event) => {
						event.preventDefault();
						this._onEstimateTagChangeRequest();
					},
				},
				chartLineIcon,
			)
		);
	}

	_renderDeleteButtonOn(html) {
		return (
			this._onDeletionRequest &&
			html.div(
				{
					class: "action-button trash-button",
					"data-test": "delete",
					click: (event) => {
						event.preventDefault();
						this._showDeleteConfirmationDialog();
					},
				},
				trashAltIcon,
			)
		);
	}

	_renderFooterOn(html) {
		html.div(
			{
				class: "footer",
				title: this._forecast.getName(),
			},
			this._renderNameOn.bind(this),
			this._renderLastEditOn.bind(this),
		);
	}

	_renderNameOn(html) {
		html.div({ class: "name" }, this._forecast.getName());
	}

	_renderLastEditOn(html) {
		html.div(
			html.span(
				{ class: "date" },
				formatAsDateAndTime(this._forecast.getLastEditDate()),
			),
		);
	}

	_showDeleteConfirmationDialog() {
		this._confirmDeletion({
			name: this._forecast.getName(),
			callback: this._onDeletionRequest,
		});
	}
}
